import { useMemo, useState } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import { DynamicTable } from '@/components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '@/components/custom/table/dynamic-table-types';
import type { AuditLog } from '@cms/models';
import { formatAuditChangesSummary } from '@cms/utils';
import { useAuditLogsPaginated } from '@/lib/hooks/useAuditLogs';
import { usePermission } from '@/lib/rbac/usePermission';
import { SearchFilter } from '@/components/custom/filters';
import { formatDateTime } from '@/lib/utils';
import type { PaginationState, SortingState } from '@tanstack/react-table';

function displayUser(log: AuditLog): string {
  return log.user?.displayName || log.user?.name || log.user?.email || '—';
}

function displayRole(log: AuditLog): string {
  return log.user?.role?.name || '—';
}

function displayChanges(log: AuditLog): string {
  return formatAuditChangesSummary(log.changes);
}

export function AuditLogsPage() {
  const { col, page, tableLoading } = useAdminI18n();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [globalFilter, setGlobalFilter] = useState('');

  const canView = usePermission('settings:read');

  const currentPage = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;

  const sortBy =
    sorting[0]?.id === 'action' ||
    sorting[0]?.id === 'entityType' ||
    sorting[0]?.id === 'createdAt'
      ? (sorting[0].id as 'action' | 'entityType' | 'createdAt')
      : 'createdAt';
  const sortOrder: 'asc' | 'desc' = sorting[0]?.desc ? 'desc' : 'asc';

  const { data: listResponse, isLoading } = useAuditLogsPaginated({
    page: currentPage,
    pageSize,
    search: globalFilter || undefined,
    sortBy,
    sortOrder,
  });

  const logs = canView ? (listResponse?.data ?? []) : [];
  const totalRecords = canView ? (listResponse?.total ?? 0) : 0;

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const columns = useMemo<DynamicTableColumnConfig<AuditLog>[]>(
    () => [
      {
        id: 'no',
        header: col('no'),
        columnType: 'number',
        cell: ({ index }) => (safePage - 1) * pageSize + index + 1,
        size: 56,
        meta: { className: 'text-muted-foreground w-14' },
      },
      {
        id: 'user',
        header: col('user'),
        columnType: 'text',
        cell: ({ row }) => (
          <div className="font-medium text-foreground truncate max-w-[200px]" title={displayUser(row)}>
            {displayUser(row)}
          </div>
        ),
        meta: { className: '' },
      },
      {
        id: 'role',
        header: col('role'),
        columnType: 'text',
        cell: ({ row }) => (
          <span className="text-muted-foreground truncate max-w-[160px] inline-block" title={displayRole(row)}>
            {displayRole(row)}
          </span>
        ),
        meta: { className: '' },
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: col('action'),
        sortable: true,
        columnType: 'text',
        cell: ({ value }) => (
          <span className="font-mono text-sm text-foreground">{(value as string | null) ?? '—'}</span>
        ),
        meta: { className: '' },
      },
      {
        id: 'entityType',
        accessorKey: 'entityType',
        header: col('entity_type'),
        sortable: true,
        columnType: 'text',
        cell: ({ value }) => (
          <span className="text-muted-foreground">{(value as string | null) ?? '—'}</span>
        ),
        meta: { className: '' },
      },
      {
        id: 'entityId',
        accessorKey: 'entityId',
        header: col('entity_id'),
        columnType: 'text',
        cell: ({ value }) => (
          <span className="text-muted-foreground font-mono text-sm whitespace-nowrap">
            {(value as string | null) ?? '—'}
          </span>
        ),
        meta: { className: 'whitespace-nowrap' },
      },
      {
        id: 'changes',
        header: col('changes'),
        columnType: 'text',
        cell: ({ row }) => (
          <span
            className="text-muted-foreground font-mono text-sm truncate max-w-[280px] inline-block"
            title={displayChanges(row)}
          >
            {displayChanges(row)}
          </span>
        ),
        meta: { className: '' },
      },
      {
        id: 'ipAddress',
        accessorKey: 'ipAddress',
        header: col('ip_address'),
        columnType: 'text',
        cell: ({ value }) => (
          <span className="text-muted-foreground font-mono text-sm">
            {(value as string | null) ?? '—'}
          </span>
        ),
        meta: { className: '' },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: col('created_at'),
        sortable: true,
        columnType: 'text',
        cell: ({ value }) => formatDateTime(value as string | Date | undefined),
        meta: { className: 'text-muted-foreground whitespace-nowrap' },
      },
    ],
    [col, safePage, pageSize],
  );

  const controllers = useMemo<DynamicTableControllers>(
    () => ({
      sort: {
        state: sorting,
        onSortingChange: updaterOrValue => {
          setSorting(prev =>
            typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue
          );
          setPagination(prev => ({ ...prev, pageIndex: 0 }));
        },
      },
      pagination: {
        state: { pageIndex: safePage - 1, pageSize },
        pageCount: totalPages,
        rowCount: totalRecords,
        onPaginationChange: updaterOrValue => {
          const current = { pageIndex: safePage - 1, pageSize };
          const next =
            typeof updaterOrValue === 'function' ? updaterOrValue(current) : updaterOrValue;
          setPagination({
            pageIndex: next.pageIndex ?? 0,
            pageSize: next.pageSize ?? pageSize,
          });
        },
      },
    }),
    [sorting, safePage, pageSize, totalPages, totalRecords],
  );

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2 w-full">
      <SearchFilter
        label="Search"
        value={globalFilter}
        onChange={v => {
          setGlobalFilter(v);
          setPagination(prev => ({ ...prev, pageIndex: 0 }));
        }}
        placeholder="Search by user, action, entity, or IP..."
      />
    </div>
  );

  if (!canView) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{page('audit_logs', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('audit_logs', 'subtitle')}</p>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          You don&apos;t have permission to view audit logs.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{page('audit_logs', 'title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{page('audit_logs', 'subtitle')}</p>
      </div>

      <div className="glass-card overflow-hidden">
        <DynamicTable<AuditLog>
          features={{
            columns: true,
            rows: true,
            sorting: true,
            pagination: true,
            globalFilter: false,
            manualPagination: true,
            manualSorting: true,
            fontSize: 'sm',
          }}
          columns={columns}
          rows={{
            data: logs,
            getRowId: log => log.id,
          }}
          controllers={controllers}
          isLoading={isLoading}
          loadingMessage={tableLoading}
          emptyMessage="No audit logs found."
          toolbar={toolbar}
          pageSizeOptions={[25, 50, 100]}
        />
      </div>
    </div>
  );
}
